import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
    getReferenceCommunityId,
    getToken,
    isAdmin, isAdminOnly, resetAnswers, resetProfile,
    resetToken, setLoggedIn, setReferenceCommunityId,
    setRole,
    setFirstLogin,
    setToken,
    resetEmail,
    resetUserData,
    getInvitedBy,
    resetInvitedBy,
} from "../services/auth";
import { voleeApi } from "../api/VoleeApi";
import { PAGES } from "../routes/PAGES";
import { useHistory, useLocation } from "react-router-dom";
import LangService from "../services/LangService";
import queryString from "query-string";
import CommunityHelmet from "./CommunityHelmet";
import { applyCommunityTheme, shouldSkipSharedPalette } from "../services/themeDocument";
import { isAutoOnlyPath, resolveThemeMode, THEME_PREFERENCES } from "../services/themeMode";
import { preferenceFromStorageEvent, readCachedPreference, readSystemDark, writeCachedPreference } from "../services/themeSession";
import { getDomain, setResolvedCommunityDomain } from "utils";
import { getFirstLogin } from "../services/auth";
import { usePermissions } from "./permissions";

export function resolvePostLoginBackTo(backTo) {
    if (typeof backTo !== 'string' || !backTo.trim()) {
        return null;
    }
    const trimmed = backTo.trim();
    if (trimmed.startsWith('/')) {
        return trimmed;
    }
    const pagePath = PAGES[trimmed.toUpperCase().replace(/-/g, '_')];
    if (pagePath) {
        return pagePath === PAGES.MANAGE_CHATS ? PAGES.ADMIN_CHATS : pagePath;
    }
    if (trimmed.includes('/')) {
        return '/' + trimmed;
    }
    return null;
}

/** Admin home after login. A deeplink wins. Otherwise member home, unless the account cannot use member pages. */
export function resolveAdminPostLoginPath(backTo, adminOnly) {
    const resolved = typeof backTo === 'string' ? resolvePostLoginBackTo(backTo) : null;
    if (resolved) {
        return resolved;
    }
    return adminOnly ? PAGES.EVENT : PAGES.MY_COMMUNITIES;
}

const defaultSteps = {
    stepsConfig: {
        profile: {
            visible: true,
        },
        lookingFor: {
            visible: true
        },
        contacts: {
            visible: true
        },
        networks: {
            visible: true
        },
        avatar: {
            visible: true
        },
        about: {
            visible: true
        },
    }
}

const defaultContacts = {
    contactsConfig: [],
    requiredContacts: ["FACEBOOK", "LINKEDIN"],
    requiredContactsMin: 1,
    shownContacts: ["TELEGRAM", "INSTAGRAM", "LINKEDIN", "FACEBOOK"],
}

const defaultBrand = {
    senderEmail: "",
    brandConfig: {
        title: "",
        logoUrl: "",
        siteUrl: ""
    }
}
/** Fallback when API has no langChoices (e.g. before load or on error) */
export const DEFAULT_LANG_CHOICES = ['EN', 'RU'];

const normalizeUiLang = (lang) => {
    const upper = lang ? String(lang).toUpperCase() : 'EN';
    return upper === 'RU' ? 'RU' : 'EN';
};

/** Multilingual = user can pick language in header; otherwise community.lang is fixed. */
export const isMultilingualCommunity = (langChoices) => (
    Array.isArray(langChoices) && langChoices.length > 1
);

/** Languages for admin/content editors (Quill fields) based on community settings. */
export const getCommunityEditorLanguages = (community) => {
    if (isMultilingualCommunity(community?.langChoices)) {
        return (community.langChoices || DEFAULT_LANG_CHOICES).map((choice) => String(choice || '').toUpperCase());
    }
    return [normalizeUiLang(community?.lang)];
};

/** Single-language communities ignore Telegram/browser localStorage (e.g. EN Telegram + RU community). */
export const resolveCommunityUiLang = (community, storedLang = null) => {
    const communityLang = normalizeUiLang(community?.lang);
    if (!isMultilingualCommunity(community?.langChoices)) {
        return communityLang;
    }
    const localLang = storedLang ? String(storedLang).toUpperCase() : null;
    const choicesUpper = (community.langChoices || []).map((c) => String(c || '').toUpperCase());
    if (localLang && choicesUpper.includes(localLang)) {
        return localLang;
    }
    return communityLang;
};

const defaultCommunity = {
    domain: "volee",
    landingTitle: "Start networking with members of the community",
    imgUrl: "",
    landingCopy: "",
    title: "",
    lang: "en",
    langChoices: null,
    transparent: false,
    ...defaultContacts,
    ...defaultSteps,
    ...defaultBrand
}

const communityContext = createContext({});

export function ProvideCommunity({ children }) {
    const community = useProvideCommunity();

    return (
        <communityContext.Provider value={community}>
            <CommunityHelmet community={community.community} />
            {children}
        </communityContext.Provider>
    );
}

export function useCommunity() {
    return useContext(communityContext);
}

export function useCurrentUser() {
    const { currentUser } = useContext(communityContext);
    return currentUser;
}

export function useUserProfile() {
    const { userProfile } = useContext(communityContext);
    return userProfile;
}

export function useProvideCommunity() {
    const history = useHistory();
    const location = useLocation();
    const [currentUser, setCurrentUser] = useState({})
    const [userProfile, setUserProfile] = useState({})
    const [community, setCommunityState] = useState(defaultCommunity);
    const [navigationConfig, setNavigationConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uiLangRevision, setUiLangRevision] = useState(0);
    const langChoicesRef = useRef(null);
    const communityLangRef = useRef(defaultCommunity.lang);

    const applyCommunityUiLang = (communityConfig) => {
        const placeholder = !communityConfig?.id
            && communityConfig?.langChoices == null
            && normalizeUiLang(communityConfig?.lang) === 'EN';
        if (placeholder) {
            return LangService.lang;
        }
        const resolved = resolveCommunityUiLang(
            communityConfig,
            localStorage.getItem("local"),
        );
        if (LangService.lang !== resolved) {
            LangService.lang = resolved;
            setUiLangRevision((revision) => revision + 1);
        }
        return resolved;
    };

    const setCommunity = (community) => {
        const langChoices = community.langChoices;
        const localStorageLang = localStorage.getItem("local");
        langChoicesRef.current = langChoices;
        communityLangRef.current = community.lang;
        applyCommunityUiLang(community);
        // Attach server-provided options array if available
        const options = Array.isArray(community.options) ? community.options : (community.options || []);
        setCommunityState({
            ...defaultContacts,
            ...defaultSteps,
            ...defaultBrand,
            ...community,
            options
        });
    }

    const loadCommunityOptions = async (communityId) => {
        if (!communityId) return;
        const cacheKey = `community_options_${communityId}`;
        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const raw = JSON.parse(cached);
                const items = Array.isArray(raw) ? raw : [];
                const options = items.map(i => (i && typeof i === 'object' ? i.optionType : i) || i);
                setCommunityState(prev => ({ ...prev, options, optionsRaw: items }));
            }
        } catch { /* noop */ }

        try {
            const items = await voleeApi.community.options(communityId);
            const options = Array.isArray(items) ? items.map(i => (i && typeof i === 'object' ? i.optionType : i) || i) : [];
            localStorage.setItem(cacheKey, JSON.stringify(items));
            setCommunityState(prev => ({ ...prev, options, optionsRaw: items }));
        } catch (e) {
            // ignore errors; rely on cached or empty
            console.warn('Failed to load community options', e);
        }
    }

    const updateCurrentUser = async () => {
        applyCommunityUiLang({
            lang: communityLangRef.current,
            langChoices: langChoicesRef.current,
        });

        const updatedCurrentUser = await voleeApi.general.currentUser();
        const userLang = updatedCurrentUser.preferredLang;
        const langChoices = langChoicesRef.current;

        if (isMultilingualCommunity(langChoices) && userLang) {
            const currentLang = LangService.lang;
            const currentUpper = currentLang ? String(currentLang).toUpperCase() : "";
            const userUpper = String(userLang).toUpperCase();
            const choicesUpper = langChoices.map((c) => String(c || "").toUpperCase());
            if (choicesUpper.includes(userUpper) && !choicesUpper.includes(currentUpper)) {
                LangService.lang = userUpper;
                setUiLangRevision((revision) => revision + 1);
            }
        }

        gtag('set', {
            user_id: updatedCurrentUser.userId
        })
        
        // Check if user is on an explicit community subdomain
        const currentDomain = getDomain();
        const isOnExplicitSubdomain = currentDomain && currentDomain !== 'volee' && currentDomain !== '';
        const currentCommunityId = getReferenceCommunityId();
        
        // Only redirect if user is NOT on an explicit subdomain AND their first connected community
        // doesn't match the current community. If they're on an explicit subdomain, respect their choice.
        if (updatedCurrentUser.communityUrl &&
            updatedCurrentUser.connectedCommunities &&
            updatedCurrentUser.connectedCommunities[0] &&
            updatedCurrentUser.connectedCommunities[0] !== currentCommunityId &&
            !isOnExplicitSubdomain) {
            // User is not on an explicit subdomain, redirect to their default community
            const token = getToken();
            const firstLogin = getFirstLogin();
            resetUserData();
            window.location.replace(`${updatedCurrentUser.communityUrl}?token=${token}&firstLogin=${firstLogin}`);
            return;
        }
        setCurrentUser(updatedCurrentUser);
        return updatedCurrentUser;
    }

    const updateUserProfile = async (participantId) => {
        const updatedUserProfile = await voleeApi.participant.getProfile(participantId);
        setUserProfile(updatedUserProfile);
    }

    const updateCommunityData = (refCommunity) => {
        setResolvedCommunityDomain(refCommunity?.domain);
        refCommunity.id && setReferenceCommunityId(refCommunity.id);
        setCommunity(refCommunity);
        gtag('set', {
            community: refCommunity.domain
        })
        // Load options with cache for navbar decisions
        if (refCommunity.id) {
            return loadCommunityOptions(refCommunity.id).then(() => true);
        }
        return true;
    }

    const loadNavigationConfig = async (domain) => {
        try {
            if (domain && domain !== "volee") {
                const navConfig = await voleeApi.navigation.get(domain);
                if (navConfig && typeof navConfig === "object") {
                    setNavigationConfig(navConfig);
                    return;
                }
            }
        } catch (e) {
            // Navigation config is optional, fall back to default
            console.log('No custom navigation config found for domain:', domain);
        }
        setNavigationConfig(null);
    }

    const fetchCommunityData = async () => {
        setResolvedCommunityDomain('');
        let wasFoundCommunity = false;
        const domain = getDomain();
        if (domain) {
            wasFoundCommunity = await voleeApi.community.getByDomain(domain)
                .then(updateCommunityData)
                .catch(() => false);
        }

        const q = queryString.parse(location.search)
        if (q.token) {
            resetUserData();
            setToken(q.token);
            setFirstLogin(q.firstLogin);
        }
        const domainFromQuery = typeof q.domain === 'string' ? q.domain.trim() : '';
        if (!wasFoundCommunity && domainFromQuery) {
            wasFoundCommunity = await voleeApi.community.getByDomain(domainFromQuery)
                .then(updateCommunityData)
                .catch(() => false);
        }
        const referenceCommunityId = q.communityId || getReferenceCommunityId();
        if (!wasFoundCommunity && referenceCommunityId) {
            wasFoundCommunity = await voleeApi.community.get(referenceCommunityId)
                .then(updateCommunityData)
                .catch(() => false);
        }

        if (!wasFoundCommunity) {
            updateCommunityData(defaultCommunity);
        }

        // Load navigation configuration
        await loadNavigationConfig(domain || defaultCommunity.domain);
    }

    const resetData = async (backTo) => {
        setCurrentUser({});
        if (backTo) {
            history.push(backTo || PAGES.WELCOME);
        }
        await fetchCommunityData();
    }

    useEffect(() => {
        // The placeholder community has no id and lang "en". Applying it on the
        // first paint overwrites the saved choice before the real community loads.
        if (!community?.id) {
            return;
        }
        applyCommunityUiLang(community);
    }, [community.id, community.lang, community.langChoices]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchCommunityData();

            // Update current user
            const token = getToken();
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const { connectedParticipants, enabled, onboardingResume } = await updateCurrentUser();
                try {
                    const perms = await voleeApi.permission.my();
                    setRole((perms || []).join(','));
                } catch (e) {
                    setRole('');
                }

                if (isAdmin()) {
                    // proceed to whatever page is needed
                    return;
                }

                const participantId = connectedParticipants?.[0];
                if (!participantId) {
                    const pagesForRedirect = [PAGES.MY_COMMUNITIES, PAGES.PARTICIPANTS, PAGES.EVENT_LIST, "/event/"];

                    if (pagesForRedirect.some(page => location.pathname.includes(page))) {
                        console.log('redirecting to questions');
                        sessionStorage.setItem('onboardingRedirect', location.pathname + location.search);
                        history.push(PAGES.QUESTIONS);
                        return
                    }

                    // Don't redirect to questions if user is already on onboarding page
                    if (location.pathname === PAGES.ONBOARDING) {
                        return;
                    }

                    history.push(PAGES.QUESTIONS);
                    return;
                }
                const onboardingPath = location.pathname === PAGES.QUESTIONS
                    || location.pathname === PAGES.ONBOARDING
                    || location.pathname === PAGES.LAST_STEP
                    || location.pathname === PAGES.CONFIRMED;
                if (enabled === false && !onboardingPath) {
                    if (onboardingResume === 'PROFILE') {
                        history.push(PAGES.ONBOARDING);
                        return;
                    }
                    if (onboardingResume === 'QUESTIONS') {
                        history.push(PAGES.QUESTIONS);
                        return;
                    }
                    history.push(PAGES.PROFILE);
                    return;
                }
                await updateUserProfile(connectedParticipants[0]);

            } catch (e) {
                // There is a token, but old, need only to reset expired token without redirecting to login page
                if (location.pathname === PAGES.AUTH_MAGIC) {
                    return;
                }
                resetToken();
            } finally {
                setLoading(false);
            }

        })()
    }, [location.pathname]);

    const [themeMode, setThemeMode] = useState('light');
    const [systemDark, setSystemDark] = useState(() => {
        try {
            return readSystemDark();
        } catch (error) {
            return false;
        }
    });

    useEffect(() => {
        const domain = community?.domain || getDomain();
        const hasSession = Boolean(getToken());
        const serverPreference = THEME_PREFERENCES.includes(currentUser?.themePreference)
            ? currentUser.themePreference
            : null;
        const preference = serverPreference || readCachedPreference(domain) || 'AUTO';
        if (hasSession && serverPreference && !isAutoOnlyPath(location.pathname)) {
            writeCachedPreference(domain, serverPreference);
        }
        const mode = resolveThemeMode({
            pathname: location.pathname,
            hasSession,
            preference,
            systemDark,
        });
        applyCommunityTheme(community, mode, {
            skipSharedPalette: shouldSkipSharedPalette(location.pathname, community),
        });
        setThemeMode(mode);
    }, [community, currentUser?.themePreference, location.pathname, systemDark]);

    useEffect(() => {
        let media = null;
        try {
            media = window.matchMedia('(prefers-color-scheme: dark)');
        } catch (error) {
            media = null;
        }
        const onMedia = (event) => setSystemDark(event.matches);
        const domain = community?.domain || getDomain();
        const onStorage = (event) => {
            const next = preferenceFromStorageEvent(event, domain);
            if (!next) {
                return;
            }
            setCurrentUser((prev) => ({ ...prev, themePreference: next }));
        };
        if (media?.addEventListener) {
            media.addEventListener('change', onMedia);
        } else if (media?.addListener) {
            media.addListener(onMedia);
        }
        window.addEventListener('storage', onStorage);
        return () => {
            if (media?.removeEventListener) {
                media.removeEventListener('change', onMedia);
            } else if (media?.removeListener) {
                media.removeListener(onMedia);
            }
            window.removeEventListener('storage', onStorage);
        };
    }, [community?.domain]);

    const updateThemePreference = async (next) => {
        if (!THEME_PREFERENCES.includes(next)) {
            throw new Error('Unsupported theme preference');
        }
        const domain = community?.domain || getDomain();
        const previous = THEME_PREFERENCES.includes(currentUser?.themePreference)
            ? currentUser.themePreference
            : 'AUTO';
        setCurrentUser((prev) => ({ ...prev, themePreference: next }));
        writeCachedPreference(domain, next);
        try {
            await voleeApi.general.saveThemePreference(next);
        } catch (error) {
            setCurrentUser((prev) => ({ ...prev, themePreference: previous }));
            writeCachedPreference(domain, previous);
            throw error;
        }
    };

    return {
        currentUser,
        userProfile,
        updateCurrentUser,
        updateUserProfile,
        updateThemePreference,
        themeMode,
        fetchCommunityData,
        community,
        setCommunity,
        navigationConfig,
        loading,
        setLoading,
        resetData,
        uiLangRevision,
        applyCommunity: updateCommunityData,
    };
}

const magicAuth = async (authContext) => {
    return voleeApi.auth.magicAuth({
        url_auth_token: authContext.token,
        back_to: authContext.backTo,
    })
}
const googleAuth = async (authContext) => {
    return voleeApi.auth.loginGoogle({
        token: authContext.token,
    });
}
const passwordAuth = async (authContext) => {
    const form = {
        login: authContext.email,
        password: authContext.password,
    };
    return voleeApi.auth.login(form);
}

const otpAuth = async (authContext) => {
    const form = {
        login: authContext.email,
        password: authContext.password,
    };
    return voleeApi.auth.otp.validate(form);
}

const authHandlers = {
    'MAGIC': magicAuth,
    'GOOGLE': googleAuth,
    'EMAIL': passwordAuth,
    'OTP': otpAuth,
}

/*
authContext = {
    authType,
    email?,
    password?,
    token?,
    backTo?,
    introFor?
}
*/
export function useLogin() {
    const history = useHistory();
    const { updateCurrentUser, fetchCommunityData } = useCommunity();
    const { refreshPermissions } = usePermissions() || {};

    const tryAcceptInvitation = async () => {
        try {
            const invitedBy = getInvitedBy();
            if (invitedBy) {
                await voleeApi.auth.acceptInvite(invitedBy);
            }
        } catch (e) {
            console.warn(e);
        }
        finally {
            resetInvitedBy();
        }
    }

    const tryConnectTelegram = async (userId) => {
        if (userId) {
            //We've got a userId - user has logged in
            const { chatId, username } = window.parsedTelegramUserInfo;
            if (chatId) {
                //We've got a chatId - this is a Telegram MiniApp
                try {
                    const result = await voleeApi.telegram.connect({ userId, chatId, username });
                    console.debug('Telegram connected:', result);
                } catch (e) {
                    console.warn('Telegram connection error:', e);
                }
            }
        }
    }

    return async (authContext) => {
        const { token, firstLogin } = await authHandlers[authContext.authType](authContext);
        const sessionBackTo = sessionStorage.getItem('redirectToAfterLogin');
        // Don't clear redirectToAfterLogin here — keep it for onboarding redirect when user has no participants
        const backTo = authContext.backTo || sessionBackTo;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
            console.log('[Login] Redirect info:', { 
                authContextBackTo: authContext.backTo, 
                sessionBackTo, 
                finalBackTo: backTo 
            });
        }
        const introFor = authContext.introFor;
        setToken(token);
        setFirstLogin(firstLogin);
        setLoggedIn();
        resetProfile();
        resetAnswers();
        resetEmail();

        await fetchCommunityData();

        const currentUser = await updateCurrentUser();
        //It happens in case of redirect to another community
        if (!currentUser) {
            return;
        }
        if (typeof refreshPermissions === 'function') {
            await refreshPermissions();
        }
        const admin = isAdmin();

        if (admin) {
            if (sessionBackTo) {
                sessionStorage.removeItem('redirectToAfterLogin');
            }
            return history.push(resolveAdminPostLoginPath(backTo, isAdminOnly()));
        }

        if (firstLogin) {
            await tryAcceptInvitation();
        }

        if (!currentUser.connectedParticipants.length) {
            await tryConnectTelegram(currentUser.userId);
            // Save event (or other) path for redirect after onboarding; Onboarding.jsx will use it
            if (typeof backTo === 'string' && backTo.startsWith('/')) {
                sessionStorage.setItem('onboardingRedirect', backTo);
                return history.push(backTo, { introMessageForId: introFor });
            }
            return history.push(PAGES.QUESTIONS);
        }
        // Has participants — clear redirect so we don't leave stale value
        if (sessionBackTo) {
            sessionStorage.removeItem('redirectToAfterLogin');
        }
        // Check for redirect path - prioritize it over default MY_COMMUNITIES
        if (typeof backTo === 'string' && backTo.trim()) {
            const resolved = resolvePostLoginBackTo(backTo);
            if (resolved) {
                return history.push(resolved, { introMessageForId: introFor });
            }
        }
        // Default fallback
        return history.push(PAGES.MY_COMMUNITIES);

    }
}
